import { useRef, useState } from "react";
import {
  BadgeCheck,
  Calendar,
  Check,
  ExternalLink,
  Eye,
  Loader2,
  Paperclip,
  Play,
  Send,
  Upload,
} from "lucide-react";
import { Badge, Button, Tooltip } from "@/components/ui";
import { StatusBanner } from "@/components/marketplace";
import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import type { Deliverable, OfferParty, ReviewDecision } from "@/hooks/useCollaborations";
import { uploadCollaborationAsset } from "@/utils/uploadAsset";
import { PLATFORM_LABELS } from "../constants";

/**
 * Status tone. VERIFIED is the only green — it is the only "done".
 *
 * PUBLISHED is deliberately steel rather than green: it is the creator's claim
 * that a post is live, not the claim checked. Colouring it as success would let
 * a brand read the card as finished and stop, which is exactly the confusion
 * the verify step exists to prevent.
 */
const STATUS_TONE: Record<Deliverable["status"], "neutral" | "warning" | "changes" | "accent" | "success"> = {
  PENDING: "neutral",
  SUBMITTED: "warning",
  REVISION_REQUESTED: "changes",
  APPROVED: "accent",
  PUBLISHED: "accent",
  VERIFIED: "success",
};

const VIDEO = /\.(mp4|mov|webm|m4v)(\?|$)/i;

export interface DeliverableActions {
  submitDraft: (id: string, assetUrls: string[]) => Promise<void>;
  review: (id: string, decision: ReviewDecision, note?: string) => Promise<void>;
  markPublished: (id: string, url: string, proofUrl?: string) => Promise<void>;
  verify: (id: string) => Promise<void>;
}

/**
 * One deliverable, and whichever single action its state and the viewer's side
 * make available.
 *
 * Only one move is ever offered at a time. The state machine is linear and the
 * two sides alternate, so a card showing three buttons would be showing at
 * least two that will fail — and this screen is meant to be the answer to
 * "what happens next", not another thing to interpret.
 *
 * Every action carries a tooltip that states its CONSEQUENCE, never its own
 * name. "Camila puede publicar — el pago sigue retenido" is worth a hover;
 * "Aprobar el entregable" is not.
 */
export default function DeliverableCard({
  deliverable,
  collaborationId,
  viewerParty,
  maxRevisions,
  counterpartyName,
  actions,
  busy,
}: {
  deliverable: Deliverable;
  collaborationId: string;
  viewerParty: OfferParty;
  /** From the campaign. Used only for the "rounds left" copy — the cap is enforced server-side. */
  maxRevisions?: number;
  counterpartyName?: string;
  actions: DeliverableActions;
  busy: boolean;
}) {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<"submit" | "review" | "publish" | null>(null);

  const isCreator = viewerParty === "CREATOR";
  const { status } = deliverable;

  const run = async (action: () => Promise<void>) => {
    setError(null);
    try {
      await action();
      setOpen(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : t("marketplace.collaboration.deliverable.failed"));
    }
  };

  const overdue =
    deliverable.dueDate != null &&
    status !== "VERIFIED" &&
    new Date(deliverable.dueDate) < new Date();

  // Derived from the same numbers the server enforces with, so the copy and the
  // mutation cannot disagree. Undefined when the campaign's cap is unknown.
  const roundsLeft =
    maxRevisions === undefined
      ? undefined
      : Math.max(0, maxRevisions - deliverable.revisionCount);

  const draft = deliverable.draftAssetUrls[0] ?? null;

  return (
    <div className="rounded-card border border-neutral-200 bg-white px-5 py-[18px] shadow-card-rest">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-card-title-sm text-neutral-900">
            {deliverable.quantity}× {t(`marketplace.formats.${deliverable.format}`)}
          </p>
          <p className="mt-0.5 text-fine text-neutral-500">
            {PLATFORM_LABELS[deliverable.platform]}
          </p>
        </div>
        <Badge variant={STATUS_TONE[status]} stamp className="shrink-0">
          {t(`marketplace.collaboration.deliverableStatus.${status}`)}
        </Badge>
      </div>

      {deliverable.dueDate && (
        <p
          className={cn(
            "mt-2 flex items-center gap-1.5 text-fine",
            overdue ? "font-medium text-danger-fg" : "text-neutral-500",
          )}
        >
          <Calendar className="size-3.5" />
          {new Date(deliverable.dueDate).toLocaleDateString("es", {
            day: "numeric",
            month: "short",
          })}
          {overdue && ` · ${t("marketplace.collaboration.deliverable.overdue")}`}
        </p>
      )}

      {/* The draft, shown rather than linked. A list of "Archivo 1" links makes
          reviewing a video a trip to another tab, which is where reviews go to
          die. */}
      {draft && (
        <a
          href={draft}
          target="_blank"
          rel="noopener noreferrer"
          className="group relative mt-3 flex aspect-16/10 items-center justify-center overflow-hidden rounded-panel border border-neutral-200 bg-neutral-100"
        >
          {VIDEO.test(draft) ? (
            <>
              <video
                src={draft}
                muted
                playsInline
                preload="metadata"
                className="absolute inset-0 size-full object-cover"
              />
              <span className="adv-glass-play relative flex size-13 items-center justify-center rounded-full text-steel-600 transition-transform group-hover:scale-105">
                <Play className="size-[18px] translate-x-px fill-current" strokeWidth={0} />
              </span>
            </>
          ) : (
            <img
              src={draft}
              alt={t("marketplace.collaboration.deliverable.draftPreview")}
              loading="lazy"
              className="absolute inset-0 size-full object-cover"
            />
          )}

          <span className="absolute bottom-2.5 left-2.5 rounded-pill bg-neutral-900/70 px-2.5 py-1 text-micro font-medium text-white backdrop-blur-[8px]">
            {t("marketplace.collaboration.deliverable.draftPreview")}
          </span>
        </a>
      )}

      {/* Any further assets stay as links — the first one is the review. */}
      {deliverable.draftAssetUrls.length > 1 && (
        <ul className="mt-2 flex flex-wrap gap-3">
          {deliverable.draftAssetUrls.slice(1).map((url, i) => (
            <li key={url}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-fine text-steel-600 hover:text-steel-500"
              >
                <Paperclip className="size-3" />
                {t("marketplace.collaboration.deliverable.asset", { n: i + 2 })}
              </a>
            </li>
          ))}
        </ul>
      )}

      {/* What is true right now and what happens next — never a bare status. */}
      <StateBanner
        status={status}
        isCreator={isCreator}
        counterpartyName={counterpartyName}
        reviewNote={deliverable.reviewNote}
      />

      {deliverable.publishedUrl && (
        <a
          href={deliverable.publishedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 inline-flex items-center gap-1.5 text-fine text-steel-600 hover:text-steel-500"
        >
          <ExternalLink className="size-3" />
          {t("marketplace.collaboration.deliverable.viewPost")}
        </a>
      )}

      {/* The evidence the brand is being asked to verify against. It was stored
          and never shown, which left "verify" a question about a link the brand
          had to open in another tab — and a pasted URL is not evidence. */}
      {deliverable.proofScreenshotUrl && (
        <a
          href={deliverable.proofScreenshotUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-3 block"
        >
          <img
            src={deliverable.proofScreenshotUrl}
            alt={t("marketplace.collaboration.deliverable.viewProof")}
            className="max-h-48 w-auto rounded-panel border border-neutral-200 object-contain"
            loading="lazy"
          />
        </a>
      )}

      {deliverable.metrics && Object.keys(deliverable.metrics).length > 0 && (
        <dl className="mt-3 grid grid-cols-3 gap-3 border-t border-neutral-100 pt-3">
          {Object.entries(deliverable.metrics).map(([key, value]) => (
            <div key={key}>
              <dt className="adv-label">
                {t(`marketplace.collaboration.metrics.${key}`, { defaultValue: key })}
              </dt>
              <dd className="adv-mono mt-1 text-figure-sm text-neutral-900">
                {value.toLocaleString("es")}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {error && (
        <StatusBanner tone="danger" className="mt-3">
          {error}
        </StatusBanner>
      )}

      {/* ── The one available move ─────────────────────────────────────── */}

      {isCreator && (status === "PENDING" || status === "REVISION_REQUESTED") && (
        <SubmitDraftForm
          collaborationId={collaborationId}
          open={open === "submit"}
          onOpen={() => setOpen("submit")}
          busy={busy}
          onSubmit={(urls) => run(() => actions.submitDraft(deliverable.id, urls))}
        />
      )}

      {!isCreator && status === "SUBMITTED" && (
        <ReviewForm
          canRequestChanges={deliverable.canRequestChanges}
          roundsLeft={roundsLeft}
          counterpartyName={counterpartyName}
          open={open === "review"}
          onOpen={() => setOpen("review")}
          busy={busy}
          onDecide={(decision, note) =>
            run(() => actions.review(deliverable.id, decision, note))
          }
        />
      )}

      {isCreator && status === "APPROVED" && (
        <PublishForm
          collaborationId={collaborationId}
          open={open === "publish"}
          onOpen={() => setOpen("publish")}
          busy={busy}
          onPublish={(url, proof) => run(() => actions.markPublished(deliverable.id, url, proof))}
        />
      )}

      {!isCreator && status === "PUBLISHED" && (
        <div className="mt-3 border-t border-neutral-100 pt-3">
          <Tooltip label={t("marketplace.collaboration.deliverable.tip.verify")}>
            <Button
              fullWidth
              disabled={busy}
              onClick={() => run(() => actions.verify(deliverable.id))}
            >
              {busy ? <Loader2 className="animate-spin" /> : <BadgeCheck />}
              {t("marketplace.collaboration.deliverable.verify")}
            </Button>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

/**
 * The banded state message. It exists so neither side has to infer what a
 * status word means for them — most of all `PUBLISHED`, which is a claim
 * awaiting a check and reads to a brand like the end of the job.
 */
function StateBanner({
  status,
  isCreator,
  counterpartyName,
  reviewNote,
}: {
  status: Deliverable["status"];
  isCreator: boolean;
  counterpartyName?: string;
  reviewNote: string | null;
}) {
  const { t } = useTranslation();
  const k = "marketplace.collaboration.deliverable.banner";

  if (status === "REVISION_REQUESTED") {
    return (
      <StatusBanner tone="changes" className="mt-3" title={t(`${k}.revisionTitle`)}>
        {reviewNote}
      </StatusBanner>
    );
  }

  if (status === "VERIFIED") {
    return (
      <StatusBanner tone="success" className="mt-3" title={t(`${k}.verifiedTitle`)}>
        {isCreator ? t(`${k}.verifiedCreatorBody`) : t(`${k}.verifiedBrandBody`)}
      </StatusBanner>
    );
  }

  if (isCreator && status === "PENDING") {
    return (
      <StatusBanner tone="info" className="mt-3" title={t(`${k}.pendingCreatorTitle`)}>
        {t(`${k}.pendingCreatorBody`)}
      </StatusBanner>
    );
  }

  if (isCreator && status === "SUBMITTED") {
    // Deliberately does NOT promise the 48-hour auto-approval the prototypes
    // show. No such job exists — there is no timer, no cron and no server-side
    // expiry — and a promise the platform will not keep is worse on a trust
    // product than no promise at all. Restore the copy when F3 ships it.
    return (
      <StatusBanner
        tone="warning"
        className="mt-3"
        title={t(`${k}.submittedCreatorTitle`, {
          name: counterpartyName ?? t("marketplace.collaboration.turn.brand"),
        })}
      >
        {t(`${k}.submittedCreatorBody`)}
      </StatusBanner>
    );
  }

  if (isCreator && status === "APPROVED") {
    return (
      <StatusBanner tone="info" className="mt-3" title={t(`${k}.approvedCreatorTitle`)}>
        {t(`${k}.approvedCreatorBody`)}
      </StatusBanner>
    );
  }

  if (!isCreator && status === "PUBLISHED") {
    return (
      <StatusBanner tone="warning" className="mt-3" title={t(`${k}.publishedBrandTitle`)}>
        {t(`${k}.publishedBrandBody`)}
      </StatusBanner>
    );
  }

  // Waiting on the other side, with nothing state-specific to add: say so
  // rather than showing nothing, which reads as a screen that failed to load.
  if (isWaiting(status, isCreator)) {
    return (
      <p className="mt-3 flex items-center gap-1.5 border-t border-neutral-100 pt-3 text-fine text-neutral-400">
        <Eye className="size-3.5" />
        {t("marketplace.collaboration.deliverable.waiting")}
      </p>
    );
  }

  return null;
}

/** True when the viewer has nothing to do and the other side does. */
function isWaiting(status: Deliverable["status"], isCreator: boolean): boolean {
  if (status === "VERIFIED") return false;
  if (isCreator) return status === "SUBMITTED" || status === "PUBLISHED";
  return status === "PENDING" || status === "REVISION_REQUESTED" || status === "APPROVED";
}

function SubmitDraftForm({
  collaborationId,
  open,
  onOpen,
  busy,
  onSubmit,
}: {
  collaborationId: string;
  open: boolean;
  onOpen: () => void;
  busy: boolean;
  onSubmit: (assetUrls: string[]) => Promise<void>;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [urls, setUrls] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  if (!open) {
    return (
      <div className="mt-3">
        <Tooltip label={t("marketplace.collaboration.deliverable.tip.submitDraft")}>
          <Button fullWidth onClick={onOpen}>
            <Upload />
            {t("marketplace.collaboration.deliverable.submitDraft")}
          </Button>
        </Tooltip>
      </div>
    );
  }

  const onFiles = async (files: FileList | null) => {
    if (!files?.length) return;
    setUploadError(null);
    setUploading(true);

    try {
      const uploaded = await Promise.all(
        Array.from(files).map((file) =>
          uploadCollaborationAsset(file, collaborationId, "drafts"),
        ),
      );
      setUrls((prev) => [...prev, ...uploaded.map((u) => u.url)]);
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : t("marketplace.collaboration.upload.failed"));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="mt-3 space-y-2.5 border-t border-neutral-100 pt-3">
      <input
        ref={inputRef}
        type="file"
        multiple
        accept="image/*,video/mp4,video/quicktime,video/webm"
        onChange={(e) => onFiles(e.target.files)}
        className="hidden"
      />

      {/* Dashed means "not yet real" — the same signal as the "+ Agregar" chip
          and the undo bar. */}
      <button
        type="button"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        className="flex w-full flex-col items-center justify-center gap-1.5 rounded-panel border border-dashed border-neutral-300 px-4 py-7 text-neutral-600 transition-colors hover:border-steel-600 hover:bg-steel-50 hover:text-steel-600 disabled:opacity-45"
      >
        {uploading ? (
          <Loader2 className="size-5 animate-spin" />
        ) : (
          <Upload className="size-5" strokeWidth={2} />
        )}
        <span className="text-meta font-semibold">
          {uploading
            ? t("marketplace.collaboration.upload.uploading")
            : t("marketplace.collaboration.upload.choose")}
        </span>
      </button>

      {urls.length > 0 && (
        <p className="text-fine text-neutral-500">
          {t("marketplace.collaboration.upload.ready", { count: urls.length })}
        </p>
      )}

      {uploadError && <StatusBanner tone="danger">{uploadError}</StatusBanner>}

      <Tooltip label={t("marketplace.collaboration.deliverable.tip.sendForReview")}>
        <Button
          fullWidth
          disabled={busy || uploading || urls.length === 0}
          onClick={() => onSubmit(urls)}
        >
          {busy ? <Loader2 className="animate-spin" /> : <Send />}
          {t("marketplace.collaboration.deliverable.sendForReview")}
        </Button>
      </Tooltip>
    </div>
  );
}

function ReviewForm({
  canRequestChanges,
  roundsLeft,
  counterpartyName,
  open,
  onOpen,
  busy,
  onDecide,
}: {
  canRequestChanges: boolean;
  roundsLeft?: number;
  counterpartyName?: string;
  open: boolean;
  onOpen: () => void;
  busy: boolean;
  onDecide: (decision: ReviewDecision, note?: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [note, setNote] = useState("");

  if (!open) {
    return (
      <div className="mt-3">
        <Tooltip label={t("marketplace.collaboration.deliverable.tip.review")}>
          <Button fullWidth onClick={onOpen}>
            <Eye />
            {t("marketplace.collaboration.deliverable.review")}
          </Button>
        </Tooltip>
      </div>
    );
  }

  const approveTip = counterpartyName
    ? t("marketplace.collaboration.deliverable.tip.approve", { name: counterpartyName })
    : t("marketplace.collaboration.deliverable.tip.approveNoName");

  const changesTip = !canRequestChanges
    ? t("marketplace.collaboration.deliverable.tip.requestChangesNone")
    : roundsLeft !== undefined
      ? t("marketplace.collaboration.deliverable.tip.requestChanges", { count: roundsLeft })
      : t("marketplace.collaboration.deliverable.requestChanges");

  return (
    <div className="mt-3 space-y-2.5 border-t border-neutral-100 pt-3">
      <textarea
        rows={3}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        placeholder={t("marketplace.collaboration.deliverable.notePlaceholder")}
        className="w-full resize-none rounded-control border border-neutral-200 bg-white px-3 py-2.5 text-control text-neutral-900 transition-colors placeholder:text-neutral-400 hover:border-neutral-300"
      />

      {/* The cap is enforced in DeliverableService.review; this is computed from
          the same numbers so the button and the mutation cannot disagree. */}
      {canRequestChanges && roundsLeft !== undefined && (
        <p className="text-fine text-neutral-500">
          {t("marketplace.collaboration.deliverable.revisionsLeft", { count: roundsLeft })}
        </p>
      )}

      {!canRequestChanges && (
        <StatusBanner tone="warning">
          {t("marketplace.collaboration.deliverable.capExplainer")}
        </StatusBanner>
      )}

      <div className="flex items-center gap-2">
        <Tooltip label={approveTip}>
          <Button
            className="flex-1"
            disabled={busy}
            onClick={() => onDecide("APPROVE", note.trim() || undefined)}
          >
            {busy ? <Loader2 className="animate-spin" /> : <Check />}
            {t("marketplace.collaboration.deliverable.approve")}
          </Button>
        </Tooltip>

        {/* align="end": this button sits flush with the card's right edge, and
            a centred tip gets clipped there. */}
        <Tooltip label={changesTip} align="end">
          <Button
            variant="secondary"
            className="flex-1"
            disabled={busy || !canRequestChanges}
            onClick={() => onDecide("REQUEST_CHANGES", note.trim() || undefined)}
          >
            {t("marketplace.collaboration.deliverable.requestChanges")}
          </Button>
        </Tooltip>
      </div>
    </div>
  );
}

function PublishForm({
  collaborationId,
  open,
  onOpen,
  busy,
  onPublish,
}: {
  collaborationId: string;
  open: boolean;
  onOpen: () => void;
  busy: boolean;
  onPublish: (url: string, proofUrl?: string) => Promise<void>;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [url, setUrl] = useState("");
  const [proofUrl, setProofUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  if (!open) {
    return (
      <div className="mt-3">
        <Tooltip label={t("marketplace.collaboration.deliverable.tip.markPublished")}>
          <Button fullWidth onClick={onOpen}>
            <ExternalLink />
            {t("marketplace.collaboration.deliverable.markPublished")}
          </Button>
        </Tooltip>
      </div>
    );
  }

  const onProofFile = async (files: FileList | null) => {
    const file = files?.[0];
    if (!file) return;
    setUploading(true);
    setUploadError(null);
    try {
      const { url: uploaded } = await uploadCollaborationAsset(file, collaborationId, "proof");
      setProofUrl(uploaded);
    } catch (e) {
      // Without this the button just returned to "attach" and the creator had
      // no idea the upload had failed — they would publish with no proof and
      // find out when the brand refused to verify.
      setUploadError(e instanceof Error ? e.message : String(e));
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div className="mt-3 space-y-2.5 border-t border-neutral-100 pt-3">
      <input
        type="url"
        value={url}
        onChange={(e) => setUrl(e.target.value)}
        placeholder="https://instagram.com/p/..."
        className="w-full rounded-control border border-neutral-200 bg-white px-3 py-2.5 text-control text-neutral-900 transition-colors placeholder:text-neutral-400 hover:border-neutral-300"
      />

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        onChange={(e) => onProofFile(e.target.files)}
        className="hidden"
      />

      <button
        type="button"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        className="inline-flex w-full items-center justify-center gap-1.5 rounded-control border border-dashed border-neutral-300 px-3 py-2.5 text-fine font-medium text-neutral-600 transition-colors hover:border-steel-600 hover:bg-steel-50 hover:text-steel-600 disabled:opacity-45"
      >
        {uploading ? (
          <Loader2 className="size-3.5 animate-spin" />
        ) : (
          <Upload className="size-3.5" />
        )}
        {proofUrl
          ? t("marketplace.collaboration.deliverable.proofAttached")
          : t("marketplace.collaboration.deliverable.attachProof")}
      </button>

      {uploadError && <StatusBanner tone="danger">{uploadError}</StatusBanner>}

      <Tooltip label={t("marketplace.collaboration.deliverable.tip.confirmPublished")}>
        <Button
          fullWidth
          disabled={busy || uploading || !url.trim()}
          onClick={() => onPublish(url.trim(), proofUrl ?? undefined)}
        >
          {busy ? <Loader2 className="animate-spin" /> : <Send />}
          {t("marketplace.collaboration.deliverable.confirmPublished")}
        </Button>
      </Tooltip>
    </div>
  );
}
